/**
 * Module summary:
 * This page displays and edits the user's profile, summarizes recent activity,
 * and manages security settings such as trusted devices, passkeys, and MFA
 * enrollment from one account-centered view.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import AppShell from '../layout/AppShell';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { getProfile, saveProfile, listAssessmentsByUser, syncAssessmentsByUser, type AssessmentRecord } from '../../services/data';
import { MfaEnrollment } from '../molecules/MfaEnrollment';
import { requiresProfileCompletion } from '../../services/auth/authService';
import {
  isPlatformAuthenticatorSupported,
  registerWebAuthnCredential,
  listTrustedDevices,
  revokeTrustedDevice,
  listWebauthnCredentials,
  revokeWebauthnCredential,
  type TrustedDeviceRecord,
  type WebAuthnCredentialRecord,
} from '../../services/auth/securitySessionService';
import {
  INPUT_LIMITS,
  assert,
  normalizeUsPhoneE164,
  sanitizeSingleLineText,
} from '../../utils/inputSecurity';
import './pages.css';

function formatPhoneForDisplay(value?: string): string {
  const digits = (value || '').replace(/\D/g, '');
  // Reformat stored digits into a friendlier display string without changing the underlying saved value.
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value || 'Not provided';
}

export const UserProfile: React.FC = () => {
  const { user, updateProfileDetails } = useAuth();
  const navigate = useNavigate();
  const initial = getProfile();
  const displayName = user?.displayName || initial.displayName || '';
  const organization = (user?.organization || initial.organization || '').trim() || 'Not provided';
  const phone = formatPhoneForDisplay((user?.phone || initial.phone || '').trim());
  const organizationAddress = (user?.organizationAddress || user?.address || initial.address || '').trim() || 'Not provided';

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    displayName,
    organization: (user?.organization || initial.organization || '').trim(),
    phone: (user?.phone || initial.phone || '').trim(),
    organizationAddress: (user?.organizationAddress || user?.address || initial.address || '').trim(),
  });
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [profileError, setProfileError] = useState('');
  const [trustedDevices, setTrustedDevices] = useState<TrustedDeviceRecord[]>([]);
  const [webauthnCredentials, setWebauthnCredentials] = useState<WebAuthnCredentialRecord[]>([]);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityError, setSecurityError] = useState('');
  const [securitySuccess, setSecuritySuccess] = useState('');
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityStatus, setActivityStatus] = useState<'all' | 'completed' | 'in-progress'>('all');
  const [activitySort, setActivitySort] = useState<'updatedDesc' | 'updatedAsc' | 'scoreDesc' | 'scoreAsc' | 'nameAsc' | 'nameDesc'>('updatedDesc');
  const [assessments, setAssessments] = useState<AssessmentRecord[]>([]);

  useEffect(() => {
    if (!user?.id) {
      // Clear profile activity when no user is active so stale rows do not remain visible.
      setAssessments([]);
      return;
    }
    // Show cached assessment history immediately, then replace it with the latest synced activity.
    setAssessments(listAssessmentsByUser(user.id));
    void syncAssessmentsByUser(user.id).then(setAssessments);
  }, [user?.id]);

  const filteredAssessments = useMemo(() => {
    const needle = activitySearch.trim().toLowerCase();
    const filtered = assessments.filter((assessment) => {
      const rowStatus = assessment.status === 'completed' ? 'completed' : 'in-progress';
      // Combine status and text filtering so the activity list responds to both controls together.
      const statusMatch = activityStatus === 'all' || rowStatus === activityStatus;
      const searchMatch = !needle || [assessment.name, assessment.address, assessment.buildingType, assessment.id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
      return statusMatch && searchMatch;
    });

    return filtered.sort((a, b) => {
      // Sort the already filtered activity rows according to the chosen profile-page sort order.
      switch (activitySort) {
        case 'updatedAsc':
          return a.updatedAt.localeCompare(b.updatedAt);
        case 'updatedDesc':
          return b.updatedAt.localeCompare(a.updatedAt);
        case 'scoreAsc':
          return (a.score ?? 0) - (b.score ?? 0);
        case 'scoreDesc':
          return (b.score ?? 0) - (a.score ?? 0);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'nameAsc':
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }, [assessments, activitySearch, activityStatus, activitySort]);
  const visibleAssessments = useMemo(() => filteredAssessments.slice(0, 5), [filteredAssessments]);
  const userId = user?.id || initial.userId || 'N/A';
  const profileNeedsCompletion = requiresProfileCompletion(user || null);

  const handleEdit = useCallback(() => {
    // Re-seed the edit form from the latest user/profile values each time edit mode opens.
    setEditForm({
      displayName,
      organization: (user?.organization || initial.organization || '').trim(),
      phone: (user?.phone || initial.phone || '').trim(),
      organizationAddress: (user?.organizationAddress || user?.address || initial.address || '').trim(),
    });
    setEditing(true);
    setSuccessMsg('');
    setProfileError('');
  }, [displayName, initial.organization, initial.phone, initial.address, user?.organization, user?.phone, user?.organizationAddress, user?.address]);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setSuccessMsg('');
    setProfileError('');
  }, []);

  const handleSave = useCallback(async () => {
    const sanitizedDisplayName = sanitizeSingleLineText(editForm.displayName, INPUT_LIMITS.displayName);
    const sanitizedOrganization = sanitizeSingleLineText(editForm.organization, INPUT_LIMITS.organization);
    const sanitizedAddress = sanitizeSingleLineText(editForm.organizationAddress, INPUT_LIMITS.address);
    const normalizedPhone = editForm.phone.trim() ? normalizeUsPhoneE164(editForm.phone) : undefined;
    if (!sanitizedDisplayName) {
      setProfileError('Display name is required.');
      return;
    }
    setSaving(true);
    setProfileError('');
    try {
      assert(!editForm.phone.trim() || Boolean(normalizedPhone), 'Please enter a valid 10-digit phone number.');
      await updateProfileDetails({
        displayName: sanitizedDisplayName,
        organization: sanitizedOrganization || undefined,
        phone: normalizedPhone,
        organizationAddress: sanitizedAddress || undefined,
      });
      saveProfile({
        // Mirror the saved profile locally so the next page load has immediate data even before backend sync.
        displayName: sanitizedDisplayName,
        organization: sanitizedOrganization || undefined,
        phone: normalizedPhone,
        address: sanitizedAddress || undefined,
        userId,
      });
      setEditing(false);
      setSuccessMsg('Profile updated successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Could not save profile changes.');
    } finally {
      setSaving(false);
    }
  }, [editForm, updateProfileDetails, userId]);

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    // Update only the targeted form field while keeping the rest of the draft edit state intact.
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));

  const loadSecurityData = useCallback(async () => {
    if (!user) return;
    setSecurityLoading(true);
    setSecurityError('');
    try {
      // Load trusted devices and passkeys in parallel because the page needs both security lists at once.
      const [devices, credentials] = await Promise.all([listTrustedDevices(), listWebauthnCredentials()]);
      setTrustedDevices(devices);
      setWebauthnCredentials(credentials);
    } catch (err) {
      setSecurityError(err instanceof Error ? err.message : 'Failed to load security data');
    } finally {
      setSecurityLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Check device passkey support and load current security artifacts when the page opens.
    void isPlatformAuthenticatorSupported().then(setPasskeySupported);
    void loadSecurityData();
  }, [loadSecurityData]);

  const handleRegisterFaceId = useCallback(async () => {
    setSecurityLoading(true);
    setSecurityError('');
    setSecuritySuccess('');
    try {
      // Create a readable label so the new passkey entry is easier to identify later in the UI.
      const label = `Passkey ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      await registerWebAuthnCredential(label);
      setSecuritySuccess('Passkey registered.');
      await loadSecurityData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Face ID registration failed';
      if (message.toLowerCase().includes('existing passkey')) {
        // Refresh the list anyway so the page reflects the passkey that already exists on this device.
        await loadSecurityData();
        setSecuritySuccess('This device already has a passkey for this account.');
      } else {
        setSecurityError(message);
      }
    } finally {
      setSecurityLoading(false);
    }
  }, [loadSecurityData]);

  const handleRevokeTrustedDevice = useCallback(async (deviceHash: string) => {
    setSecurityLoading(true);
    setSecurityError('');
    try {
      // Revoke the selected trusted device so future logins from that browser require stronger verification again.
      await revokeTrustedDevice(deviceHash);
      await loadSecurityData();
    } catch (err) {
      setSecurityError(err instanceof Error ? err.message : 'Failed to revoke trusted device');
    } finally {
      setSecurityLoading(false);
    }
  }, [loadSecurityData]);

  const handleRevokeCredential = useCallback(async (credIdHash: string) => {
    setSecurityLoading(true);
    setSecurityError('');
    setSecuritySuccess('');
    try {
      // Remove the selected passkey and then refresh the backend list so the UI reflects the true server state.
      await revokeWebauthnCredential(credIdHash);
      setWebauthnCredentials((prev) => prev.filter((cred) => cred.credIdHash !== credIdHash));
      setSecuritySuccess('Passkey revoked. You can now register a new one.');
      await loadSecurityData();
    } catch (err) {
      setSecurityError(err instanceof Error ? err.message : 'Failed to revoke WebAuthn credential');
    } finally {
      setSecurityLoading(false);
    }
  }, [loadSecurityData]);

  const passkeyCount = webauthnCredentials.length;
  // Expose the current passkey count for summary cards and quick security status messaging.

  return (
    <AppShell title="My Profile" isDashboard>
      <div className="content-page profile-page">
        <section className="content-page__hero content-page__hero--dark profile-hero">
          <div className="profile-hero__identity">
            <div className="profile-hero__avatar">{(displayName || 'G').charAt(0).toUpperCase()}</div>
            <div>
              <p className="content-page__eyebrow">Profile</p>
              <h1 className="content-page__title">{displayName || 'Guest'}</h1>
              <p className="content-page__subtitle">{user?.email || 'No email available'}</p>
            </div>
          </div>
          <div className="profile-hero__meta">
            <span>ID: {userId}</span>
          </div>
        </section>

        {successMsg && <section className="profile-banner">{successMsg}</section>}
        {profileNeedsCompletion && (
          <section className="profile-banner">
            Complete your full name, phone number, organization, and organization address before continuing.
          </section>
        )}

        <section className="content-card profile-card profile-card--account">
          <div className="content-card__header">
            <div>
              <p className="content-card__eyebrow">Account Details</p>
              <h2 className="content-card__title profile-account__title">Profile information</h2>
            </div>
            {!editing && (
              <button type="button" className="report-row__button" onClick={handleEdit}>
                Edit Profile
              </button>
            )}
          </div>

          {editing ? (
            <div className="profile-form">
              {profileError && <p className="create-account__error">{profileError}</p>}
              <div className="profile-form__grid">
                <label className="contact-field">
                  <span>Full Name</span>
                  <input value={editForm.displayName} onChange={updateField('displayName')} />
                </label>
                <label className="contact-field">
                  <span>Phone Number</span>
                  <input value={editForm.phone} onChange={updateField('phone')} placeholder="(555) 123-4567" />
                </label>
                <label className="contact-field">
                  <span>Organization</span>
                  <input value={editForm.organization} onChange={updateField('organization')} />
                </label>
              </div>
              <label className="contact-field">
                <span>Organization Address</span>
                <input value={editForm.organizationAddress} onChange={updateField('organizationAddress')} />
              </label>
              <div className="profile-form__actions">
                <button type="button" className="report-row__button" onClick={handleCancel} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="dashboard-btn dashboard-btn--solid" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-stats">
              <article className="profile-stat profile-stat--name">
                <span>Full Name</span>
                <strong>{displayName || 'Not provided'}</strong>
              </article>
              <article className="profile-stat profile-stat--email">
                <span>Email</span>
                <strong>{user?.email || 'Not provided'}</strong>
              </article>
              <article className="profile-stat profile-stat--organization">
                <span>Organization</span>
                <strong>{organization}</strong>
              </article>
              <article className="profile-stat profile-stat--phone">
                <span>Phone</span>
                <strong>{phone}</strong>
              </article>
              <article className="profile-stat profile-stat--address">
                <span>Organization Address</span>
                <strong>{organizationAddress}</strong>
              </article>
            </div>
          )}
        </section>

        <section className="content-grid content-grid--two">
          <article className="content-card profile-card">
            <div className="content-card__header">
              <div>
                <p className="content-card__eyebrow">Security</p>
                <h2 className="content-card__title">Two-Factor Authentication</h2>
              </div>
            </div>
            <MfaEnrollment
              userPhone={user?.phone || initial.phone}
              onPhoneVerified={async (phoneE164) => {
                await updateProfileDetails({
                  phone: phoneE164,
                  displayName: displayName || undefined,
                  organization: organization !== 'Not provided' ? organization : undefined,
                  organizationAddress: organizationAddress !== 'Not provided' ? organizationAddress : undefined,
                });
                saveProfile({
                  ...initial,
                  displayName,
                  organization: organization !== 'Not provided' ? organization : undefined,
                  phone: phoneE164,
                  address: organizationAddress !== 'Not provided' ? organizationAddress : undefined,
                  userId,
                });
              }}
            />
          </article>

          <article className="content-card profile-card">
            <div className="content-card__header">
              <div>
                <p className="content-card__eyebrow">Passkeys</p>
                <h2 className="content-card__title">Passkey & Device Security</h2>
              </div>
            </div>

            {securityError && <p className="profile-note profile-note--error">{securityError}</p>}
            {securitySuccess && <p className="profile-note profile-note--success">{securitySuccess}</p>}

            <div className="profile-passkey">
              <p className="content-card__copy">
                Enroll a passkey on this device to sign in later with Face ID, Touch ID, or Windows Hello.
              </p>
              {passkeySupported ? (
                <>
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--solid"
                    onClick={handleRegisterFaceId}
                    disabled={securityLoading}
                  >
                    {passkeyCount > 0 ? 'Add Another Passkey' : 'Enable Face ID / Biometrics'}
                  </button>
                  {passkeyCount > 0 && (
                    <p className="content-card__copy">
                      {passkeyCount} passkey{passkeyCount === 1 ? '' : 's'} registered. Add one per device so sign-in works across iPhone, Mac, Windows, and tablets.
                    </p>
                  )}
                </>
              ) : (
                <p className="content-card__copy">Passkeys are not available on this browser or device.</p>
              )}
            </div>

            <div className="profile-security-list">
              <div>
                <h3 className="profile-security-list__title">Trusted Devices</h3>
                {trustedDevices.length === 0 ? (
                  <p className="content-card__copy">No remembered devices.</p>
                ) : (
                  trustedDevices.map((device) => (
                    <div key={device.deviceHash} className="profile-security-item">
                      <div>
                        <strong>{device.label || 'Remembered device'}</strong>
                        <span>{device.lastSeenIP || 'Unknown IP'} - {device.lastSeenUA || 'Unknown browser'}</span>
                      </div>
                      <button type="button" className="report-row__button report-row__button--danger" onClick={() => handleRevokeTrustedDevice(device.deviceHash)}>
                        Revoke
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div>
                <h3 className="profile-security-list__title">Registered Passkeys</h3>
                {webauthnCredentials.length === 0 ? (
                  <p className="content-card__copy">No passkeys registered yet.</p>
                ) : (
                  webauthnCredentials.map((cred) => (
                    <div key={cred.credIdHash} className="profile-security-item">
                      <div>
                        <strong>{cred.label || 'Platform authenticator'}</strong>
                        <span>Credential hash: {cred.credIdHash?.slice(0, 12)}...</span>
                      </div>
                      <button type="button" className="report-row__button report-row__button--danger" onClick={() => handleRevokeCredential(cred.credIdHash)}>
                        Revoke
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </article>
        </section>

        <section className="content-card profile-card">
          <div className="content-card__header">
            <div>
              <p className="content-card__eyebrow">History</p>
              <h2 className="content-card__title">Assessment activity</h2>
            </div>
          </div>

          {assessments.length > 0 && (
            <div className="reports-filters reports-filters--profile">
              <div className="reports-filters__grid">
                <label className="reports-filters__field reports-filters__field--search">
                  <span>Search</span>
                  <input
                    type="text"
                    value={activitySearch}
                    onChange={(e) => setActivitySearch(e.target.value)}
                    placeholder="Search by name, address, type, or ID"
                  />
                </label>
                <label className="reports-filters__field">
                  <span>Status</span>
                  <select value={activityStatus} onChange={(e) => setActivityStatus(e.target.value as typeof activityStatus)}>
                    <option value="all">All</option>
                    <option value="completed">Completed</option>
                    <option value="in-progress">In Progress</option>
                  </select>
                </label>
                <label className="reports-filters__field">
                  <span>Sort</span>
                  <select value={activitySort} onChange={(e) => setActivitySort(e.target.value as typeof activitySort)}>
                    <option value="updatedDesc">Newest First</option>
                    <option value="updatedAsc">Oldest First</option>
                    <option value="scoreDesc">Highest Score</option>
                    <option value="scoreAsc">Lowest Score</option>
                    <option value="nameAsc">Name A-Z</option>
                    <option value="nameDesc">Name Z-A</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {assessments.length === 0 ? (
            <div className="reports-empty">
              <p className="content-card__copy">Start your first inspection to see activity here.</p>
            </div>
          ) : filteredAssessments.length === 0 ? (
            <div className="reports-empty">
              <p className="content-card__copy">No assessment activity matches your filters.</p>
            </div>
          ) : (
            <div className="reports-list">
              {visibleAssessments.map((assessment) => (
                <article key={assessment.id} className="report-row">
                  <div className="report-row__main">
                    <h3 className="report-row__title">{assessment.name}</h3>
                    <p className="report-row__meta">
                      {new Date(assessment.updatedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {assessment.address ? ` - ${assessment.address}` : ''}
                    </p>
                  </div>
                  <div className="report-row__actions">
                    <span className={`dashboard-status dashboard-status--${assessment.status === 'completed' ? 'complete' : 'active'}`}>
                      {assessment.status === 'completed' ? `Score ${assessment.score ?? 0}%` : 'In Progress'}
                    </span>
                    <button
                      type="button"
                      className={assessment.status === 'completed' ? 'dashboard-btn dashboard-btn--solid report-row__cta' : 'report-row__button'}
                      onClick={() =>
                        navigate(
                          assessment.status === 'completed'
                            ? `/report/${assessment.id}`
                            : `/assessment?id=${encodeURIComponent(assessment.id)}`
                        )
                      }
                    >
                      {assessment.status === 'completed' ? 'View Report' : 'Continue'}
                    </button>
                  </div>
                </article>
              ))}
              {filteredAssessments.length > 5 && (
                <p className="content-card__copy">
                  Showing 5 of {filteredAssessments.length} matching assessments.
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
};

export default UserProfile;


